// src/resources/Drivers/DriverEdit.js
import React, { useEffect, useState } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  AutocompleteInput,
  DateInput,
  FormDataConsumer,
} from "react-admin";

import { db, storage } from "../../firebase/firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { DriverExpiryInputs } from "./DriverFields";
import { DriverAssignmentsSection } from "./DriverAssignmentsSection";

export const DriverEdit = () => {
  const [companies, setCompanies] = useState([]);

  // -----------------------------------------------------------
  // Load Companies (Client, Comfort Line, Lolia, etc.)
  // -----------------------------------------------------------
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setCompanies(list);
    });
    return () => unsub();
  }, []);

  // -----------------------------------------------------------
  // File Upload Helper
  // -----------------------------------------------------------
  const uploadFile = async (file, folder) => {
    if (!file || !file.rawFile) return null;

    const fileRef = ref(
      storage,
      `${folder}/${Date.now()}_${file.rawFile.name}`
    );

    await uploadBytes(fileRef, file.rawFile);
    return await getDownloadURL(fileRef);
  };

  // -----------------------------------------------------------
  // Save Driver Updates + File Uploads
  // -----------------------------------------------------------
  const handleSave = async (values) => {
    const driverRef = doc(db, "drivers", values.id);
    const updates = { ...values };

    const files = [
      { key: "passportFile", folder: "drivers/passport" },
      { key: "passportFileBack", folder: "drivers/passport" },
      { key: "emiratesIdFile", folder: "drivers/eid" },
      { key: "emiratesIdFileBack", folder: "drivers/eid" },
      { key: "licenseFile", folder: "drivers/license" },
      { key: "licenseFileBack", folder: "drivers/license" },
      { key: "rtaPermitFile", folder: "drivers/rtaPermit" },
    ];

    // Upload each file (if changed)
    for (const { key, folder } of files) {
      const fileInput = values[key];

      const file =
        Array.isArray(fileInput) && fileInput[0]?.rawFile
          ? fileInput[0]
          : fileInput?.rawFile
          ? fileInput
          : null;

      if (file) {
        const url = await uploadFile(file, folder);
        updates[key] = url; // save file URL
      }
    }


    
// ✅ Cancellation history (supports manual cancelled date)
const status = String(updates.status || "").toLowerCase();

if (status === "cancelled") {
  const v = updates.cancelledAt;

  // If already Firestore Timestamp, keep it
  if (v && typeof v === "object" && typeof v.seconds === "number") {
    // keep as-is
  }
  // If Date object (common from DateInput), convert to Timestamp
  else if (v instanceof Date) {
    const d = new Date(v);
    d.setHours(0, 0, 0, 0);
    updates.cancelledAt = Timestamp.fromDate(d);
  }
  // If string like "YYYY-MM-DD", convert to Timestamp
  else if (typeof v === "string" && v.trim()) {
    const d = new Date(v.slice(0, 10) + "T00:00:00");
    updates.cancelledAt = Timestamp.fromDate(d);
  }
  // If empty, set "now"
  else {
    updates.cancelledAt = serverTimestamp();
  }
} else {
  updates.cancelledAt = null;
}


    await updateDoc(driverRef, updates);
    alert("✅ Employee updated successfully!");

    // Redirect back
    window.location.hash = "#/drivers";
  };

  // -----------------------------------------------------------
  // Render UI
  // -----------------------------------------------------------
  return (
    <Edit title="Edit Employee">
      <SimpleForm onSubmit={handleSave}>

        {/* Basic Info */}
        <TextInput source="fullName" label="Full Name" fullWidth />
        <TextInput source="phone" label="Phone Number" fullWidth />

        {/* Company Selection */}
        <AutocompleteInput
          source="company"
          label="Company"
          choices={companies}
          optionText="name"
          optionValue="name"
          fullWidth
        />

        {/* Status */}
        <SelectInput
          source="status"
          label="Status"
          choices={[
            { id: "Active", name: "Active" },
            { id: "Cancelled", name: "Cancelled" },
          ]}
        />

<FormDataConsumer>
  {({ formData }) =>
    String(formData?.status || "").toLowerCase() === "cancelled" ? (
      <DateInput
        source="cancelledAt"
        label="Cancelled Date"
        helperText="Optional. If empty, it will be set to today when saving."
        fullWidth
      />
    ) : null
  }
</FormDataConsumer>


        {/* License / Visa Expiry Inputs */}
        <DriverExpiryInputs />

        {/* ---------------------------------------------------- */}
        {/* 🔥 Driver → Car Assignments MUST be LAST in Form    */}
        {/* ---------------------------------------------------- */}
        <DriverAssignmentsSection />

      </SimpleForm>
    </Edit>
  );
};
